import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { InvoicesService } from 'shared/services/invoices.service';
import { APIResponse, DbActivityLog, DocumentHelper, ObjectUtils, ReceivedPaymentStatus, UserSettings, Utils, VatRateUtils } from 'fakturnia-shared';
import * as PdfMake from "pdfmake/build/pdfmake";
import { Title } from '@angular/platform-browser';
import { DialogService } from 'shared/services/dialog.service';
import { DocumentsService } from 'shared/services/documents.service';
import { DataSynchronizerService } from 'shared/services/data-synchronization.service';
import { Invoice, InvoiceAnnotations } from 'fakturnia-shared';
import { AppData } from 'fakturnia-shared';
import { KsefClientValidatorUtils } from 'shared/utils/ksef-client-validator.utils';
import { DbDocument } from 'fakturnia-shared';
import { InvoiceGenerator } from 'fakturnia-shared';
import { DocumentsApiService } from 'shared/services/api/documents.api.service';
import { ActivityLogsService } from 'shared/services/activity-logs.service';
import { MediaWatcherService } from 'shared/services/media-watcher.service';
import { SynchronizationService } from 'shared/services/synchronization.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { ActivityLogsComponent } from 'shared/components/activity-logs/activity-logs.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { SettingsService } from 'shared/services/settings.service';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { KSeFApiService } from 'shared/services/api/ksef.api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SnackbarService } from 'shared/services/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { KsefInvoicePreviewDialogComponent } from 'shared/dialogs/ksef-invoice-preview-dialog/ksef-invoice-preview-dialog.component';
import { XmlPreviewDialogComponent, XmlPreviewDialogData } from 'shared/dialogs/xml-preview-dialog/xml-preview-dialog.component';
import { SelectLanguageDialogComponent } from 'shared/dialogs/select-language-dialog/select-language-dialog.component';
import { settings } from 'environments/environment';
import { DateTime } from 'luxon';
import { DocumentTypeNamePipe } from 'shared/pipes/document-type-name.pipe';
import { DateFormatterComponent } from 'shared/components/date-formatter/date-formatter.component';
import { KsefActionsService, KsefSendState } from 'shared/services/ksef-actions.service';
import { DEMO_INVOICE_ID, DEMO_INVOICE, DEMO_DOCUMENT, DEMO_ACTIVITY_LOGS } from 'shared/data/test-invoice';

/**
 * Interfejs dla pozycji faktury z KSeF XML
 */
export interface KsefInvoiceItem {
  lp: string;
  name: string;
  unit: string;
  quantity: number;
  unitNetPrice: number;
  unitGrossPrice: number;
  netValue: number;
  vatRate: string;
  vatValue: number;
  grossValue: number;
  deliveryDate: string | null; // P_6A - data dostawy/wykonania usługi dla wiersza
  exchangeRate: number | null; // KursWaluty - kurs waluty dla wiersza
  isBeforeCorrection: boolean; // StanPrzed = 1 - wiersz sprzed korekty
}

/**
 * Interfejs dla danych płatności z KSeF XML
 */
export interface KsefPaymentInfo {
  paymentDeadline: string | null;       // Termin płatności (Termin)
  paymentMethodCode: number | null;     // Kod formy płatności (FormaPlatnosci)
  paymentMethodName: string;            // Nazwa formy płatności
  isPaid: boolean;                      // Czy zapłacono (Zaplacono = 1)
  paidDate: string | null;              // Data zapłaty (DataZaplaty)
  paidAmount: number | null;            // Kwota zapłaty (ZnsczenieZaplaty)
  bankAccountNumber: string | null;     // Numer konta bankowego (RachunekBankowy/NrRB)
}

/**
 * Interfejs dla danych faktury korygującej z KSeF XML
 */
export interface KsefCorrectionInfo {
  invoiceType: string;                  // RodzajFaktury (KOR, KOR_ZAL, etc.)
  correctionReason: string | null;      // PrzyczynaKorekty
  correctionType: number | null;        // TypKorekty (1, 2, 3)
  correctionTypeName: string;           // Nazwa typu korekty
  correctedInvoiceDate: string | null;  // DataWystFaKorygowanej
  correctedInvoiceNumber: string | null;// NrFaKorygowanej
  correctedKsefNumber: string | null;   // NrKSeFFaKorygowanej (jeśli jest)
}

/**
 * Interfejs dla pozycji zamówienia z KSeF XML (dla faktur zaliczkowych)
 */
export interface KsefOrderItem {
  lp: string;                           // NrWierszaZam - numer pozycji
  name: string;                         // P_7Z - nazwa towaru/usługi
  unit: string;                         // P_8AZ - jednostka miary
  quantity: number;                     // P_8BZ - ilość
  unitNetPrice: number;                 // P_9AZ - cena jednostkowa netto
  netValue: number;                     // P_11NettoZ - wartość netto
  vatValue: number;                     // P_11VatZ - kwota VAT
  vatRate: string;                      // P_12Z - stawka VAT
  grossValue: number;                   // obliczone: netto + VAT
  isBeforeCorrection: boolean;          // StanPrzedZ = 1 - pozycja sprzed korekty
}

/**
 * Interfejs dla danych zamówienia z KSeF XML (dla faktur zaliczkowych)
 */
export interface KsefOrderInfo {
  orderValue: number;                   // WartoscZamowienia - całkowita wartość zamówienia
  items: KsefOrderItem[];               // ZamowienieWiersz - pozycje zamówienia
  exchangeRate: number | null;          // KursWalutyZ - kurs waluty zamówienia (po korekcie)
  exchangeRateBeforeCorrection: number | null; // KursWalutyZK - kurs waluty korygowanej zaliczki
  grossAmountBeforeCorrection: number | null;  // P_15ZK - kwota brutto korygowanej faktury w PLN
}

/**
 * Interfejs dla okresu faktury z KSeF XML (OkresFa)
 */
export interface KsefPeriodInfo {
  dateFrom: string | null;              // P_6_Od - data początkowa okresu
  dateTo: string | null;                // P_6_Do - data końcowa okresu
}

/**
 * Interfejs dla danych adresowych i kontaktowych podmiotu z KSeF XML (Podmiot1/Podmiot2)
 */
export interface KsefSubjectInfo {
  countryCode: string | null;           // KodKraju (np. PL)
  addressLine1: string | null;          // AdresL1 (ulica i numer)
  addressLine2: string | null;          // AdresL2 (kod pocztowy i miasto)
  email: string | null;                 // DaneKontaktowe/Email
  phone: string | null;                 // DaneKontaktowe/Telefon
}

/**
 * Mapa kodów KSeF FormaPlatnosci na nazwy
 */
const KSEF_PAYMENT_METHODS: { [key: number]: string } = {
  1: 'Gotówka',
  2: 'Karta płatnicza',
  3: 'Bon',
  4: 'Czek',
  5: 'Kredyt',
  6: 'Przelew',
  7: 'Płatność mobilna (BLIK)'
};

/**
 * Mapa typów korekty KSeF
 */
const KSEF_CORRECTION_TYPES: { [key: number]: string } = {
  1: 'Korekta skutkująca w dacie ujęcia faktury pierwotnej',
  2: 'Korekta skutkująca w dacie wystawienia faktury korygującej',
  3: 'Korekta skutkująca w innej dacie'
};

@Component({
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, MatTooltipModule, RouterLink, ActivityLogsComponent, NgxExtendedPdfViewerModule, DocumentTypeNamePipe, DateFormatterComponent],
  selector: 'app-preview',
  templateUrl: './preview.component.html',
  styleUrls: ['./preview.component.scss']
})
export class PreviewComponent implements OnInit, OnDestroy {

  private readonly _subscriptions: Subscription[] = []

  private _id = null
  public isDemo = false
  public invoice: Invoice | null = null
  public document: DbDocument | null = null
  public previewData: any = null
  public dataUrl: any = null
  public isPdfLoaded: boolean | null = null
  public activityLogs: DbActivityLog[] = []
  public pdfPreviewHeight = '1300px'
  public pagesCount = 1
  public isSendingToKsef = false
  
  /** Overlay sukcesu - pojawiający się po wysłaniu do KSeF */
  public ksefSuccessOverlay = false;
  public ksefSuccessKsefNumber: string | null = null;
  
  // Powiązane faktury zaliczkowe (dla zamówień)
  public linkedAdvanceInvoices: DbDocument[] = [];
  
  // Powiązane zamówienie (dla faktur zaliczkowych)
  public linkedOrder: DbDocument | null = null;
  
  // Korygowany dokument (dla faktur korygujących)
  public correctedDocument: DbDocument | null = null;
  
  // Korekty tego dokumentu (dokumenty korygujące ten dokument)
  public correctionDocuments: DbDocument[] = [];
  
  // Powiązana faktura rozliczeniowa (dla faktur zaliczkowych)
  public linkedSettlementInvoice: DbDocument | null = null;
  
  // Dane XML z KSeF
  public ksefXmlContent: string | null = null;
  public ksefInvoiceItems: KsefInvoiceItem[] = [];
  public ksefPaymentInfo: KsefPaymentInfo | null = null;
  public ksefCorrectionInfo: KsefCorrectionInfo | null = null;
  public ksefOrderInfo: KsefOrderInfo | null = null;
  public ksefPeriodInfo: KsefPeriodInfo | null = null; // Okres faktury (OkresFa)
  public ksefInvoiceType: string | null = null; // RodzajFaktury (VAT, ZAL, KOR, etc.)
  public ksefSystemInfo: string | null = null; // SystemInfo z Naglowek (nazwa systemu wystawiającego)
  public ksefSellerInfo: KsefSubjectInfo | null = null; // Dane adresowe sprzedawcy z XML
  public ksefBuyerInfo: KsefSubjectInfo | null = null; // Dane adresowe nabywcy z XML
  public isLoadingKsefXml = false;

  /** Czy funkcje KSeF są włączone (środowisko + ustawienia użytkownika) */
  public get ksefEnabled(): boolean {
    return settings.ksefEnabled && (this.userSettings?.settings?.general?.enableKsefFeatures ?? true);
  }

  /**
   * Formatuje stawkę VAT do wyświetlenia.
   * Np. '0 KR' → '0% - KR', 23 → '23%', 'np I' → 'np. I'
   */
  formatVatRate(vatRate: string | number): string {
    return VatRateUtils.getDisplayValue(vatRate);
  }

  /**
   * Sprawdza czy dokument pochodzi z KSeF (nie jest lokalny)
   */
  public get isKsefDocument(): boolean {
    if (!this.document) return false;
    return DocumentHelper.isKsefDocument(this.document);
  }

  /**
   * Pobiera surowe dane z KSeF (dla faktur z source='ksef')
   */
  public get ksefRawData(): any {
    return (this.document as any)?.ksefRawData || null;
  }

  /**
   * Sprawdza czy dokument z KSeF to faktura zaliczkowa (ZAL)
   */
  public get isKsefAdvanceInvoice(): boolean {
    return this.isKsefDocument && this.ksefInvoiceType === 'ZAL';
  }

  /**
   * Sprawdza czy dokument z KSeF to korekta faktury zaliczkowej (KOR_ZAL)
   */
  public get isKsefAdvanceCorrectionInvoice(): boolean {
    return this.isKsefDocument && this.ksefInvoiceType === 'KOR_ZAL';
  }

  /**
   * Sprawdza czy zamówienie KOR_ZAL zawiera pozycje ze znacznikiem StanPrzedZ
   */
  public get hasOrderCorrectionBeforeAfterItems(): boolean {
    return this.ksefOrderInfo !== null && this.ksefOrderInfo.items.some(item => item.isBeforeCorrection);
  }

  /**
   * Pozycje zamówienia PRZED korektą (StanPrzedZ = 1)
   */
  public get ksefOrderItemsBeforeCorrection(): KsefOrderItem[] {
    return this.ksefOrderInfo?.items.filter(item => item.isBeforeCorrection) || [];
  }

  /**
   * Pozycje zamówienia PO korekcie (brak StanPrzedZ)
   */
  public get ksefOrderItemsAfterCorrection(): KsefOrderItem[] {
    return this.ksefOrderInfo?.items.filter(item => !item.isBeforeCorrection) || [];
  }

  /**
   * Sprawdza czy którakolwiek pozycja faktury ma datę dostawy (P_6A)
   * Kolumna "Data dostawy" jest wyświetlana tylko gdy istnieją takie daty
   */
  public get hasDeliveryDates(): boolean {
    return this.ksefInvoiceItems.some(item => item.deliveryDate !== null);
  }

  /**
   * Sprawdza czy którakolwiek pozycja faktury ma kurs waluty (KursWaluty)
   * Kolumna "Kurs" jest wyświetlana tylko gdy istnieją kursy waluty
   */
  public get hasExchangeRates(): boolean {
    return this.ksefInvoiceItems.some(item => item.exchangeRate !== null);
  }

  /**
   * Sprawdza czy faktura korygująca zawiera pozycje ze znacznikiem StanPrzed
   * (czyli korekta wykazująca pozycje przed i po korekcie)
   */
  public get hasCorrectionBeforeAfterItems(): boolean {
    return this.ksefCorrectionInfo !== null && this.ksefInvoiceItems.some(item => item.isBeforeCorrection);
  }

  /**
   * Pozycje faktury korygującej PRZED korektą (StanPrzed = 1)
   */
  public get ksefItemsBeforeCorrection(): KsefInvoiceItem[] {
    return this.ksefInvoiceItems.filter(item => item.isBeforeCorrection);
  }

  /**
   * Pozycje faktury korygującej PO korekcie (brak StanPrzed)
   */
  public get ksefItemsAfterCorrection(): KsefInvoiceItem[] {
    return this.ksefInvoiceItems.filter(item => !item.isBeforeCorrection);
  }

  /**
   * Pobiera identyfikator sprzedawcy (NIP lub VAT-UE) z odpowiednim prefixem
   */
  public getSellerIdentifier(): { prefix: string; value: string } {
    const rawData = this.ksefRawData;
    
    // Sprawdź czy to VAT-UE
    if (rawData?.seller?.identifier?.type === 'VatUe') {
      return {
        prefix: 'VAT-UE',
        value: rawData.seller.identifier.value || ''
      };
    }
    
    // Domyślnie NIP
    const nip = rawData?.seller?.nip || rawData?.seller?.identifier?.value || this.document?.seller?.taxId || '';
    return { prefix: 'NIP', value: nip };
  }

  /**
   * Pobiera identyfikator nabywcy (NIP lub VAT-UE) z odpowiednim prefixem
   */
  public getBuyerIdentifier(): { prefix: string; value: string } {
    const rawData = this.ksefRawData;
    const identType = rawData?.buyer?.identifier?.type;

    if (identType === 'VatUe') {
      return { prefix: 'VAT-UE', value: rawData.buyer.identifier.value || '' };
    }

    if (identType === 'Other') {
      return { prefix: 'Identyfikator podatkowy', value: rawData.buyer.identifier.value || '' };
    }

    if (identType === 'None') {
      return { prefix: 'Brak identyfikatora podatkowego', value: '' };
    }

    const nip = rawData?.buyer?.nip || rawData?.buyer?.identifier?.value || this.document?.buyer?.taxId || '';

    if (!nip) {
      return { prefix: 'Brak identyfikatora podatkowego', value: '' };
    }

    return { prefix: 'NIP', value: nip };
  }
  
  // Status połączenia KSeF
  public ksefConnectionStatus: {
    isAuthorized: boolean;
    lastError: string | null;
    lastErrorAt: Date | null;
    environment: string | null;
    nip: string | null;
  } | null = null;
  public isLoadingKsefStatus = false;

  // DEBUG: ręczne przełączanie stanu KSeF do testowania UI
  public debugKsefStatusOverride: 'success' | 'error' | 'pending' | 'not-sent' | null = null;
  
  logo: { base64: string, width: number, height: number } | null = null
  userSettings: UserSettings | null = null

  private _previousUrl: string | null = null

  constructor(
    private _documentsApiService: DocumentsApiService,
    private _invoicesService: InvoicesService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _dialogService: DialogService,
    private _documentsService: DocumentsService,
    private _title: Title,
    private _dataSynchronizerService: DataSynchronizerService,
    private _activityLogsService: ActivityLogsService,
    private _mediaWatcherService: MediaWatcherService,
    private _synchronizationService: SynchronizationService,
    private _settingsService: SettingsService,
    private _ksefApiService: KSeFApiService,
    private _snackbarService: SnackbarService,
    private _matDialog: MatDialog,
    private _location: Location,
    private _ksefActionsService: KsefActionsService
  ) {

    // Zapamiętaj poprzedni URL — sprawdź skąd nawigowaliśmy
    // Angular Router nie daje bezpośrednio dostępu do poprzedniego URL,
    // więc korzystamy z sesji przeglądarki: documents list zapisuje swój URL przy wyjściu
    this._previousUrl = sessionStorage.getItem('fakturnia_documents_list_url');

    // Subscribe to KSeF send state
    this._subscriptions.push(
      this._ksefActionsService.sendState$.subscribe((state: KsefSendState) => {
        // Aktualizuj stan tylko jeśli dotyczy aktualnego dokumentu
        if (state.documentId && this.document?._id && state.documentId !== this.document._id) return;

        this.isSendingToKsef = state.status === 'sending';

        if (state.status === 'success' && state.ksefNumber) {
          this.ksefSuccessKsefNumber = state.ksefNumber;
          this.ksefSuccessOverlay = true;

          // Automatycznie ukryj overlay po 6s
          setTimeout(() => {
            this.dismissKsefSuccessOverlay();
          }, 6000);
        }

        if (state.status === 'error') {
          this.isSendingToKsef = false;
        }
      })
    );

    this._subscriptions.push(
      this._settingsService.currentUserSettings.subscribe((userSettings: UserSettings) => {
        this.userSettings = userSettings
        this.logo = ObjectUtils.getNestedValue(userSettings, 'company.logo')
      })
    )

    this._subscriptions.push(this._dataSynchronizerService.currentData.subscribe({
      next: (data) => {
        if (data == null) return
        if (data.table == 'documents' || data.table == 'activityLogs') this._getInvoice()
      }
    }))

    this._subscriptions.push(this._route.params.subscribe({
      next: (params) => {

        if (!Utils.isDefined(params, "id")) {
          this._redirectToDocuments()
          return
        }

        this._id = params['id']
        this._getInvoice()

      }
    }))
  }

  public onPdfLoaded(ev) {
    this.isPdfLoaded = true
  }


  public onPagesLoaded(ev) {
    this.pagesCount = ev.pagesCount
  }

  ngOnInit(): void {
    // Pobierz status połączenia KSeF
    if (this.ksefEnabled) {
      this.loadKsefConnectionStatus();
    }
    
    // Subscribe to media changes
    this._subscriptions.push(
      this._mediaWatcherService.onMediaChange$
        .subscribe(({ matchingAliases }) => {
          // Check if the screen is small
          if (!matchingAliases.includes('sm')) {
            this.pdfPreviewHeight = '500px'
          }
          else if (!matchingAliases.includes('md')) {
            this.pdfPreviewHeight = '750px'
          }
          else if (!matchingAliases.includes('lg')) {
            this.pdfPreviewHeight = '900px'
          }
          else this.pdfPreviewHeight = '1300px'
        })
    )
  }


  public showSendEmailDialog() {
    if (!this.invoice) return
    
    // Sprawdź czy subskrypcja wygasła
    if (settings.enableSubscriptions && this.userSettings?.subscription?.validUntil) {
      const validUntil = DateTime.fromISO(this.userSettings.subscription.validUntil);
      if (validUntil < DateTime.now()) {
        this._router.navigate(['/konto/subskrypcje']);
        return;
      }
    }

    // Sprawdź czy użytkownik ma wymagany abonament (minimum START)
    const subscriptionStage = this.userSettings?.subscription?.stage ?? 0;
    if (subscriptionStage === 0) {
      this._dialogService.showUpgradeSubscriptionDialog({
        requiredStage: 1,
        featureName: 'Wysyłka faktur e-mailem',
        featureDescription: 'wymaga planu START lub wyższego.',
        featureIcon: 'tabler_outline:mail-share'
      });
      return;
    }
    
    this._dialogService.showSendInvoiceByEmailDialog(this.invoice)
  }

  /**
   * Pobiera status połączenia z KSeF
   */
  public loadKsefConnectionStatus(): void {
    this.isLoadingKsefStatus = true;
    this._ksefApiService.get().pipe(take(1)).subscribe({
      next: (response: any) => {
        this.isLoadingKsefStatus = false;
        if (response.success && response.data) {
          this.ksefConnectionStatus = {
            isAuthorized: response.data.isAuthorized || false,
            lastError: response.data.lastError || null,
            lastErrorAt: response.data.lastErrorAt ? new Date(response.data.lastErrorAt) : null,
            environment: response.data.environment || null,
            nip: response.data.nip || null
          };
        }
      },
      error: (error) => {
        this.isLoadingKsefStatus = false;
        console.error('Błąd podczas pobierania statusu KSeF:', error);
      }
    });
  }

  /**
   * Sprawdza czy połączenie KSeF jest aktywne
   */
  public get isKsefConnectionActive(): boolean {
    return this.ksefConnectionStatus?.isAuthorized === true;
  }

  /**
   * Przejdź do konfiguracji KSeF
   */
  public goToKsefSettings(): void {
    this._router.navigate(['/konto/ksef']);
  }

  public sendToKsef() {
    if (!this.document || this.isSendingToKsef) return;
    // Przewiń do góry — overlay sukcesu/wysyłki jest u góry strony
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this._ksefActionsService.sendToKsefWithConfirmation(this.document);
  }

  public issueAndSendToKsef() {
    if (!this.document || this.isSendingToKsef) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this._ksefActionsService.issueAndSendToKsefWithConfirmation(this.document);
  }

  /**
   * Zamknij overlay sukcesu KSeF
   */
  public dismissKsefSuccessOverlay(): void {
    this.ksefSuccessOverlay = false;
    this.ksefSuccessKsefNumber = null;
    this._ksefActionsService.resetSendState();
  }

  /**
   * Kopiuj numer KSeF z overlay sukcesu
   */
  public copyKsefNumberFromOverlay(): void {
    if (this.ksefSuccessKsefNumber) {
      navigator.clipboard.writeText(this.ksefSuccessKsefNumber);
      this._snackbarService.showSnackBar('Skopiowano numer KSeF', 'success', 2000);
    }
  }

  /**
   * Sprawdza status wysyłki do KSeF
   */
  public get ksefStatus(): 'success' | 'error' | 'pending' | 'not-sent' {
    if (this.debugKsefStatusOverride) return this.debugKsefStatusOverride;
    if (!this.document) return 'not-sent';
    return DocumentHelper.getSimpleKsefStatus(this.document);
  }

  /** DEBUG: przełącza stan KSeF cyklicznie */
  public cycleDebugKsefStatus(): void {
    const states: ('not-sent' | 'pending' | 'success' | 'error' | null)[] = ['not-sent', 'pending', 'success', 'error', null];
    const currentIdx = states.indexOf(this.debugKsefStatusOverride);
    this.debugKsefStatusOverride = states[(currentIdx + 1) % states.length];
  }
  
  /**
   * Zwraca czytelny komunikat o statusie KSeF
   */
  public get ksefStatusMessage(): string {
    if (!this.document) return 'Nie wysłano do KSeF';
    return DocumentHelper.getKsefStatusMessage(this.document);
  }

  /**
   * Sprawdza czy dokument jest fakturą proforma (NIE może być wysłana do KSeF)
   */
  public get isProforma(): boolean {
    if (!this.document) return false;
    return DocumentHelper.isProforma(this.document);
  }

  /**
   * Sprawdza czy dokument jest zamówieniem/ofertą (NIE może być wysłany do KSeF)
   */
  public get isOrderDocument(): boolean {
    if (!this.document) return false;
    return DocumentHelper.isOrderDocument(this.document);
  }

  /**
   * Sprawdza czy dokument jest szkicem (isPaid === false)
   */
  public get isDraft(): boolean {
    return this.document?.isPaid === false;
  }

  /**
   * Sprawdza czy można wystawić szkic i wysłać do KSeF (jedno kliknięcie)
   */
  public get canIssueAndSendToKsef(): boolean {
    if (!this.document || !this.isDraft) return false;
    if (!this.isKsefConnectionActive) return false;
    if (DocumentHelper.isProforma(this.document)) return false;
    if (DocumentHelper.isOrderDocument(this.document)) return false;
    // Faktura (bez VAT) nie może być wysyłana do KSeF
    if (this.document.documentType === 'Faktura') return false;
    if (this.document.ksefSent === true) return false;
    if (this.document.ksefPendingSend === true) return false;

    return true;
  }

  private _ksefWarningsCache: {role: string, warnings: string[]}[] = [];
  private _ksefWarningsInvoiceRef: any = null;

  get ksefClientWarnings(): {role: string, warnings: string[]}[] {
    if (!this.invoice) return [];
    
    // Memoize to prevent infinite loop in change detection
    if (this._ksefWarningsInvoiceRef === this.invoice) {
      return this._ksefWarningsCache;
    }
    
    this._ksefWarningsInvoiceRef = this.invoice;
    const problems: {role: string, warnings: string[]}[] = [];
    
    const sellerWarnings = KsefClientValidatorUtils.getKsefWarnings(this.invoice.seller);
    if (sellerWarnings.length > 0) {
      problems.push({ role: 'Sprzedawca', warnings: sellerWarnings });
    }
    
    const buyerWarnings = KsefClientValidatorUtils.getKsefWarnings(this.invoice.buyer);
    if (buyerWarnings.length > 0) {
      problems.push({ role: 'Nabywca', warnings: buyerWarnings });
    }
    
    if (this.invoice.hasReceiver) {
      const receiverWarnings = KsefClientValidatorUtils.getKsefWarnings(this.invoice.receiver);
      if (receiverWarnings.length > 0) {
        problems.push({ role: 'Odbiorca', warnings: receiverWarnings });
      }
    }
    
    this._ksefWarningsCache = problems;
    return problems;
  }

  /**
   * Sprawdza czy data wystawienia faktury jest wcześniejsza niż dzisiejsza
   * Jeśli tak, faktura nie może być wysłana do KSeF bez aktualizacji daty
   */
  public get isIssueDateExpired(): boolean {
    if (!this.document) return false;
    return DocumentHelper.isIssueDateExpired(this.document);
  }

  /**
   * Sprawdza czy można wysłać fakturę do KSeF
   */
  public get canSendToKsef(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canSendToKsefFull(this.document, this.isKsefConnectionActive);
  }

  /**
   * Sprawdza czy można wystawić korektę do tej faktury
   * Dostępne tylko dla faktur:
   * - Wysłanych do KSeF (mają ksefNumber)
   * - Które nie są korektami (FakturaKorygujaca)
   * - Które są fakturami VAT (nie proforma, zamówienia, oferty)
   */
  public get canCreateCorrection(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canCreateCorrection(this.document);
  }

  /**
   * Sprawdza czy można wystawić fakturę rozliczeniową dla tej faktury zaliczkowej.
   * Dostępne tylko dla wystawionych FakturaZaliczka bez powiązanej faktury rozliczeniowej.
   */
  public get canCreateSettlementInvoice(): boolean {
    if (!this.document) return false;
    return this.document.documentType === 'FakturaZaliczka'
      && this.document.isPaid === true
      && !this.document.isDeleted
      && !this.linkedSettlementInvoice;
  }

  /**
   * Sprawdza czy można edytować dokument
   * Deleguje do DocumentHelper.canEdit()
   */
  public get canEditDocument(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canEdit(this.document);
  }

  /**
   * Sprawdza czy można usunąć dokument
   * Deleguje do DocumentHelper.canDelete()
   */
  public get canDeleteDocument(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canDelete(this.document);
  }

  /**
   * Sprawdza czy można zmienić dokument na szkic
   * Deleguje do DocumentHelper.canChangeToDraft()
   */
  public get canChangeToDraft(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canChangeToDraft(this.document);
  }

  /**
   * Pomocnik do debugowania danych faktury
   */
  public logInvoiceDebugInfo(): void {
    if (!this.document) return;
    const doc = this.document as any;
    console.log('📊 ===== DEBUG INFO FAKTURY =====');
    console.log('📄 Podstawowe dane:');
    console.log('  - documentNumber:', doc.documentNumber);
    console.log('  - documentType:', doc.documentType);
    console.log('  - currency:', doc.currency || 'PLN');
    console.log('  - exchangeRate:', doc.exchangeRate);
    console.log('');
    console.log('💰 Sumy:');
    console.log('  - netSum:', doc.netSum);
    console.log('  - vatSum:', doc.vatSum);
    console.log('  - grossSum:', doc.grossSum);
    console.log('');
    console.log('📦 Produkty:');
    console.log('  - Liczba produktów:', doc.products?.length || 0);
    if (doc.products && doc.products.length > 0) {
      doc.products.forEach((p: any, i: number) => {
        console.log(`  ${i + 1}. ${p.name}`);
        console.log(`     - vatRate: ${p.vatRate}`);
        console.log(`     - netValue: ${p.netValue}`);
        console.log(`     - amountOfVat: ${p.amountOfVat}`);
        console.log(`     - grossValue: ${p.grossValue}`);
      });
    } else {
      console.log('  ❌ BRAK PRODUKTÓW!');
    }
    console.log('');
    console.log('🏢 Sprzedawca:');
    console.log('  - NIP:', doc.seller?.nip);
    console.log('  - Nazwa:', doc.seller?.name);
    console.log('  - countryCode:', doc.seller?.countryCode);
    console.log('');
    console.log('🛒 Nabywca:');
    console.log('  - NIP:', doc.buyer?.nip);
    console.log('  - Nazwa:', doc.buyer?.name);
    console.log('  - countryCode:', doc.buyer?.countryCode);
    console.log('');
    console.log('📮 Status KSeF:');
    console.log('  - ksefSent:', this.document.ksefSent);
    console.log('  - ksefStatus:', this.document.ksefStatus);
    console.log('  - ksefNumber:', doc.ksefNumber);
    console.log('  - ksefReferenceNumber:', doc.ksefReferenceNumber);
    console.log('================================');
  }

  /**
   * Odświeża activity logs dla aktualnego dokumentu
   */
  private _refreshActivityLogs(): void {
    if (!this.invoice?._id) return;
    
    const previousCount = this.activityLogs.length;
    this.activityLogs = this._activityLogsService.getInvoiceActivityLogs(this.invoice._id);
    const newCount = this.activityLogs.length;
    
    console.log(`📋 Activity logs odświeżone: ${previousCount} → ${newCount} (${newCount > previousCount ? '+' + (newCount - previousCount) : 'bez zmian'})`);
    
    if (newCount > previousCount) {
      const newLogs = this.activityLogs.slice(0, newCount - previousCount);
      console.log('🆕 Nowe activity logs:', newLogs);
    }
  }

  private _getInvoice() {

    if (!this._id) return

    // Demo mode — ładuj testowe dane zamiast z IndexedDB
    if (this._id === DEMO_INVOICE_ID) {
      this.isDemo = true;
      this.invoice = DEMO_INVOICE;
      this.document = DEMO_DOCUMENT;
      this.activityLogs = DEMO_ACTIVITY_LOGS;
      this._title.setTitle('Podgląd demo - Fakturnia.pl');
      console.log('🎯 [DEMO] Załadowano dane demo, invoice.document:', this.invoice.document);
      console.log('🎯 [DEMO] Utils.isDefined(invoice, "document"):', Utils.isDefined(this.invoice, 'document'));
      console.log('🎯 [DEMO] invoice.products:', this.invoice.products);
      console.log('🎯 [DEMO] isPdfLoaded przed renderem:', this.isPdfLoaded);
      this._renderPrewiev();
      return;
    }

    this.document = this._documentsService.getById(this._id)
    
    // Pobierz powiązane faktury zaliczkowe (dla zamówień)
    this.linkedAdvanceInvoices = [];
    this.linkedOrder = null;
    this.correctedDocument = null;
    this.correctionDocuments = [];
    
    if (this.document?.documentType === 'Zamowienie') {
      const allDocs = this._documentsService.getAllDocumentsSync();
      this.linkedAdvanceInvoices = allDocs.filter(d => 
        d.documentType === 'FakturaZaliczka' && 
        (d as any).linkedOrderId === this.document?._id &&
        !d.isDeleted
      );
      // Oznacz zamówienie jako powiązane z fakturami zaliczkowymi (potrzebne dla canEdit/canDelete)
      (this.document as any).hasLinkedAdvanceInvoices = this.linkedAdvanceInvoices.length > 0;
    }
    
    // Pobierz powiązane faktury zaliczkowe (dla FakturaRozliczeniowa)
    if (this.document?.documentType === 'FakturaRozliczeniowa' && (this.document as any).linkedAdvanceInvoiceIds?.length > 0) {
      const allDocs = this._documentsService.getAllDocumentsSync();
      this.linkedAdvanceInvoices = (this.document as any).linkedAdvanceInvoiceIds
        .map((id: string) => allDocs.find(d => d._id === id))
        .filter((d: any) => d && !d.isDeleted);
    }
    
    // Pobierz powiązane zamówienie (dla faktur zaliczkowych)
    if (this.document?.documentType === 'FakturaZaliczka' && (this.document as any).linkedOrderId) {
      this.linkedOrder = this._documentsService.getById((this.document as any).linkedOrderId);
    }
    
    // Pobierz powiązaną fakturę rozliczeniową (dla faktur zaliczkowych)
    this.linkedSettlementInvoice = null;
    if (this.document?.documentType === 'FakturaZaliczka' && (this.document as any).linkedSettlementInvoiceId) {
      const settlement = this._documentsService.getById((this.document as any).linkedSettlementInvoiceId);
      // Nie pokazuj usuniętej faktury rozliczeniowej
      this.linkedSettlementInvoice = settlement && !settlement.isDeleted ? settlement : null;
    }
    
    // Pobierz korygowany dokument (dla faktur korygujących)
    if ((this.document?.documentType === 'FakturaKorygujaca' || this.document?.documentType === 'FakturaKorygujacaZaliczkowa' || this.document?.documentType === 'FakturaKorygujacaRozliczeniowa') 
        && (this.document as any).correctedInvoiceId) {
      this.correctedDocument = this._documentsService.getById((this.document as any).correctedInvoiceId);
    }
    
    // Sprawdź czy istnieją korekty tego dokumentu (żeby zablokować edycję)
    if (this.document) {
      const allDocs = this._documentsService.getAllDocumentsSync();
      this.correctionDocuments = allDocs.filter(d => 
        (d.documentType === 'FakturaKorygujaca' || d.documentType === 'FakturaKorygujacaZaliczkowa' || d.documentType === 'FakturaKorygujacaRozliczeniowa')
        && (d as any).correctedInvoiceId === this.document?._id
        && !d.isDeleted
      );
      if (this.correctionDocuments.length > 0) {
        (this.document as any).isCorrected = true;
      }
    }

    // Dla faktur z KSeF nie pobieramy Invoice (używamy tylko Document)
    if (this.document?.source === 'ksef') {
      this.invoice = null;
      this.activityLogs = [];
      this._title.setTitle(`Podgląd ${this.document.documentNumber} - Fakturnia.pl`)
      // Nie renderujemy PDF dla faktur z KSeF - wyświetlamy HTML
      this.isPdfLoaded = true; // Oznacz jako załadowane (żeby ukryć spinner)
      
      // Sprawdź czy XML jest już zapisany w dokumencie (zsynchronizowany z backendu)
      const cachedXml = this.document.ksefMetadata?.rawXml;
      
      if (cachedXml) {
        this.ksefXmlContent = cachedXml;
        this.parseKsefXmlItems(cachedXml);
      } else if (this.document.ksefNumber && !this.ksefXmlContent) {
        // Pobierz XML z API (który zapisze go w dokumencie)
        this.loadKsefXml();
      }
      return;
    }

    this.invoice = this._invoicesService.getById(this._id)

    if (!this.invoice) {
      this._redirectToDocuments()
      return
    }

    this.activityLogs = this._activityLogsService.getInvoiceActivityLogs(this.invoice._id)
    console.log('📋 Załadowano activity logs:', this.activityLogs);
    this._title.setTitle(`Podgląd ${this.invoice.invoiceName} - Fakturnia.pl`)

    this._renderPrewiev()
  }

  private async _renderPrewiev() {
    // Ensure no other PDF creation is in progress
    if (this.isPdfLoaded === false) {
      console.warn('⚠️ [RENDER] PDF creation is already in progress. Waiting...', { isPdfLoaded: this.isPdfLoaded });
      return;
    }

    if (this.invoice == null) { console.warn('⚠️ [RENDER] invoice is null'); return; }
    if (!Utils.isDefined(this.invoice, "document")) { console.warn('⚠️ [RENDER] invoice.document is not defined'); return; }

    console.log('🎯 [RENDER] Start renderowania PDF, isPdfLoaded:', this.isPdfLoaded);
    this.isPdfLoaded = false; // Mark as loading
    
    try {
      const docDefinition = InvoiceGenerator.createPDF(this.invoice.document, this.invoice, 0, "render", this.logo, this.subscriptionType, settings.ksefQrVerifyUrl)
      console.log('🎯 [RENDER] docDefinition wygenerowany:', !!docDefinition);

      // Wrap pdfMake.createPdf in a Promise
      await new Promise<void>((resolve, reject) => {
        PdfMake.createPdf(<any>docDefinition).getBase64((dataUrl) => {
          this.dataUrl = dataUrl;
          console.log('🎯 [RENDER] dataUrl ustawiony, length:', dataUrl?.length);
          resolve(); // Resolve the Promise when PDF creation is complete
        });
      });

      console.log('🎯 [RENDER] Czekam na onPdfLoaded... isPdfLoaded:', this.isPdfLoaded);
      // Wait for the PDF to finish loading
      await this._waitForPdfToLoad();
      console.log('🎯 [RENDER] PDF załadowany!');
    } catch (err) {
      console.error('❌ [RENDER] Błąd podczas renderowania PDF:', err);
      this.isPdfLoaded = true; // Reset na wypadek błędu
    }
  }

  private _waitForPdfToLoad(): Promise<void> {
    return new Promise<void>((resolve) => {
      const checkInterval = setInterval(() => {

        if (this.isPdfLoaded) {
          clearInterval(checkInterval); // Stop checking once the PDF is loaded
          resolve();
        }
      }, 100); // Check every 100ms
    });
  }

  public markAsPaidOrTemplate(isPaid) {

    if (!this.invoice) return
    
    // Sprawdź czy dokument został wysłany do KSeF - nie można go zmieniać na szkic
    if (!isPaid && this.document?.ksefReferenceNumber) {
      this._snackbarService.showSnackBar('Nie można zmienić na szkic dokumentu wysłanego do KSeF.', 'error');
      return;
    }
    
    this._subscriptions.push(
      this._dialogService.confirmMarkAsPaidOrTemplateDialog(
        isPaid,
        [this.invoice?._id],
        isPaid ? "Oznacz jako wystawiono" : "Oznacz jako szkic",
        isPaid ? `Czy na pewno chcesz oznaczyć dokument ${this.invoice.invoiceName} jako wystawiony?` : `Czy na pewno chcesz oznaczyć dokument ${this.invoice.invoiceName} jako szkic?`
      )
        .subscribe()
    )
  }


  public print() {

    if (!this.invoice) return
    if (!Utils.isDefined(this.invoice, "document")) return

    const dialogRef = this._matDialog.open(SelectLanguageDialogComponent, {
      width: '380px',
      autoFocus: false
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(lang => {
      if (!lang || !this.invoice) return;

      const docDefinition = InvoiceGenerator.createPDF(this.invoice.document, this.invoice, 0, "render", this.logo, this.subscriptionType, settings.ksefQrVerifyUrl, lang)

      PdfMake.createPdf(<any>docDefinition).print({})

      this._documentsApiService.markDocumentsAsPrinted(this.invoice._id).pipe(take(1)).subscribe({
        next: async (response: APIResponse) => {
          if (response.success != true) return
          await this._synchronizationService.synchronize()
        }
      })
    })
  }

  public download() {

    if (!this.invoice) return
    if (!Utils.isDefined(this.invoice, "document")) return

    const dialogRef = this._matDialog.open(SelectLanguageDialogComponent, {
      width: '380px',
      autoFocus: false
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(lang => {
      if (!lang || !this.invoice) return;

      InvoiceGenerator.createPDF(this.invoice.document, this.invoice, 0, "download", this.logo, this.subscriptionType, settings.ksefQrVerifyUrl, lang)

      this._documentsApiService.markDocumentsAsDownloaded(this.invoice._id).pipe(take(1)).subscribe({
        next: async (response: APIResponse) => {
          if (response.success != true) return
          await this._synchronizationService.synchronize()
        }
      })
    })
  }

  /**
   * Pobierz UPO (Urzędowe Poświadczenie Odbioru) jako plik XML
   */
  public downloadUpo() {
    if (!this.document?.ksefUpo) return

    const fileName = this.document.documentNumber || this.document.invoiceName || `faktura-${this.document.ksefNumber || this.document._id}`;

    const blob = new Blob([this.document.ksefUpo], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `UPO-${fileName}.xml`;
    link.click();
    window.URL.revokeObjectURL(url);

    this._documentsApiService.logUpoDownloaded(this.document._id).pipe(take(1)).subscribe({
      next: () => this._synchronizationService.synchronize()
    })
  }

  /**
  Show confirmation before deleting invoice
  **/
  public confirmDeleteInvoice() {

    if (!this.invoice) return
    if (!Utils.isDefined(this.invoice, "_id")) return

    this._subscriptions.push(
      this._dialogService.confirmDeleteInvoiceDialog(this.invoice)
        .subscribe({
          next: (data) => {
            if (data != true) return
            this._redirectToDocuments()
          }
        }))
  }

  private _redirectToDocuments() {
    this._router.navigateByUrl('/konto/dokumenty')
  }

  /**
   * Otwiera dialog z podglądem faktury z KSeF
   */
  public openKsefInvoicePreview(): void {
    const ksefNumber = (this.document as any)?.ksefNumber;
    
    if (!ksefNumber) {
      this._snackbarService.showSnackBar('Brak numeru KSeF faktury', 'error', 5000);
      return;
    }

    if (!this.invoice?.invoiceName) {
      this._snackbarService.showSnackBar('Brak danych faktury', 'error', 5000);
      return;
    }

    this._matDialog.open(KsefInvoicePreviewDialogComponent, {
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      maxHeight: '900px',
      data: {
        ksefNumber: ksefNumber,
        invoiceNumber: this.invoice.invoiceName
      }
    });
  }

  /**
   * Wyświetla surowe dane JSON z KSeF w konsoli i w nowym oknie
   */
  public showKsefRawData(): void {
    const rawData = this.ksefRawData;
    
    if (!rawData) {
      this._snackbarService.showSnackBar('Brak surowych danych KSeF dla tej faktury', 'error', 5000);
      return;
    }

    // Wyloguj do konsoli
    console.group(`🔍 RAW JSON z KSeF - ${this.document?.documentNumber || 'Faktura'}`);
    console.log('KSeF Number:', this.document?.ksefNumber);
    console.log('Raw Data:', rawData);
    console.groupEnd();

    // Otwórz nowe okno z sformatowanym JSON-em
    const jsonString = JSON.stringify(rawData, null, 2);
    const newWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>RAW JSON KSeF - ${this.document?.documentNumber || 'Faktura'}</title>
          <style>
            body { 
              font-family: 'Consolas', 'Monaco', monospace; 
              background: #1e1e1e; 
              color: #d4d4d4; 
              padding: 20px; 
              margin: 0;
            }
            h1 { 
              color: #4ec9b0; 
              font-size: 16px; 
              margin-bottom: 10px;
              border-bottom: 1px solid #3c3c3c;
              padding-bottom: 10px;
            }
            .ksef-number { 
              color: #9cdcfe; 
              font-size: 12px; 
              margin-bottom: 20px;
            }
            pre { 
              background: #252526; 
              padding: 15px; 
              border-radius: 4px; 
              overflow: auto; 
              font-size: 12px;
              line-height: 1.5;
            }
            .string { color: #ce9178; }
            .number { color: #b5cea8; }
            .boolean { color: #569cd6; }
            .null { color: #569cd6; }
            .key { color: #9cdcfe; }
          </style>
        </head>
        <body>
          <h1>📄 RAW JSON z KSeF</h1>
          <div class="ksef-number">KSeF Number: ${this.document?.ksefNumber || 'N/A'}</div>
          <pre>${this._syntaxHighlightJson(jsonString)}</pre>
        </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      this._snackbarService.showSnackBar('Sprawdź konsolę przeglądarki (F12) - dane zostały wylogowane', 'info', 5000);
    }
  }

  /**
   * Podświetla składnię JSON
   */
  private _syntaxHighlightJson(json: string): string {
    return json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
        let cls = 'number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'key';
          } else {
            cls = 'string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'boolean';
        } else if (/null/.test(match)) {
          cls = 'null';
        }
        return '<span class="' + cls + '">' + match + '</span>';
      });
  }

  /**
   * Kopiuje numer KSeF do schowka
   */
  public copyKsefNumber(): void {
    const ksefNumber = this.document?.ksefNumber;
    
    if (!ksefNumber) {
      this._snackbarService.showSnackBar('Brak numeru KSeF', 'error', 3000);
      return;
    }

    navigator.clipboard.writeText(ksefNumber).then(() => {
      this._snackbarService.showSnackBar('Skopiowano numer KSeF do schowka', 'success', 3000);
    }).catch(() => {
      this._snackbarService.showSnackBar('Nie udało się skopiować', 'error', 3000);
    });
  }

  /**
   * Wyświetla XML faktury KSeF w dedykowanym dialogu
   */
  public showKsefXml(): void {
    // Użyj XML z cache lub z dokumentu
    const xmlContent = this.ksefXmlContent || this.document?.ksefMetadata?.rawXml;
    
    if (!xmlContent) {
      this._snackbarService.showSnackBar('Brak danych XML dla tej faktury. Pobierz najpierw XML.', 'error', 5000);
      return;
    }

    // Otwórz dialog z podglądem XML
    this._matDialog.open(XmlPreviewDialogComponent, {
      data: {
        xml: xmlContent,
        title: 'XML faktury KSeF',
        documentNumber: this.document?.documentNumber,
        ksefNumber: this.document?.ksefNumber
      } as XmlPreviewDialogData,
      width: '90vw',
      maxWidth: '1200px',
      height: '85vh',
      panelClass: 'xml-preview-dialog'
    });
  }

  /**
   * Pobiera XML faktury z KSeF i zapisuje jako plik
   */
  public downloadKsefXml(): void {
    const ksefNumber = this.document?.ksefNumber;
    
    if (!ksefNumber) {
      this._snackbarService.showSnackBar('Brak numeru KSeF', 'error', 3000);
      return;
    }

    // Sprawdź czy XML jest zapisany w dokumencie
    const cachedXml = this.document?.ksefMetadata?.rawXml || this.ksefXmlContent;
    if (cachedXml) {
      this.saveXmlAsFile(cachedXml);
      return;
    }

    // Jeśli nie mamy - pobierz z API (który zapisze XML na backendzie)
    if (!ksefNumber) {
      this._snackbarService.showSnackBar('Brak numeru KSeF', 'error', 3000);
      return;
    }

    this._snackbarService.showSnackBar('Pobieranie XML z KSeF...', 'info', 3000);
    
    this._ksefApiService.getInvoiceXml(ksefNumber).pipe(take(1)).subscribe({
      next: (response) => {
        const xml = response?.data;
        if (!xml) {
          this._snackbarService.showSnackBar('Nie udało się pobrać XML', 'error', 5000);
          return;
        }
        
        // Zapisz w lokalnym cache
        this.ksefXmlContent = xml;
        this.parseKsefXmlItems(xml);
        
        // Pobierz jako plik
        this.saveXmlAsFile(xml);
        
        // Wyzwól synchronizację żeby zaktualizować dokument
        this._synchronizationService.synchronize();
      },
      error: (err) => {
        console.error('Błąd pobierania XML z KSeF:', err);
        this._snackbarService.showSnackBar(
          err?.error?.message || 'Wystąpił błąd podczas pobierania XML', 
          'error', 
          5000
        );
      }
    });
  }
  
  /**
   * Zapisuje XML jako plik do pobrania
   */
  private saveXmlAsFile(xml: string): void {
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.document?.documentNumber || this.document?.ksefNumber}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    this._snackbarService.showSnackBar('Pobrano XML faktury', 'success', 3000);
  }
  
  /**
   * Pobiera XML faktury z KSeF i zapisuje go na backendzie
   * Po pobraniu wyzwala synchronizację, która zaktualizuje dokument
   */
  public loadKsefXml(): void {
    const ksefNumber = this.document?.ksefNumber;
    if (!ksefNumber) return;
    
    this.isLoadingKsefXml = true;
    
    this._ksefApiService.getInvoiceXml(ksefNumber).pipe(take(1)).subscribe({
      next: (response) => {
        this.isLoadingKsefXml = false;
        const xml = response?.data;
        if (xml) {
          this.ksefXmlContent = xml;
          this.parseKsefXmlItems(xml);
          
          // Zaktualizuj lokalny obiekt dokumentu z rawXml
          // Dzięki temu przy następnym otwarciu podglądu nie będzie ponownie pobierał XML
          if (this.document) {
            const updatedKsefMetadata = {
              ...(this.document.ksefMetadata || {}),
              rawXml: xml
            };
            this.document.ksefMetadata = updatedKsefMetadata;
            
            // Zaktualizuj również w DocumentsService (w pamięci)
            // żeby przy nawigacji między dokumentami cache był dostępny
            this._documentsService.updateDocumentInMemory(this.document._id, {
              ksefMetadata: updatedKsefMetadata
            });
            
            console.log('[KSeF] XML zapisany lokalnie w document.ksefMetadata.rawXml i w DocumentsService');
          }
          
          // Wyzwól synchronizację żeby pobrać zaktualizowany dokument z backendu (zapisze też do Dexie)
          console.log('[KSeF] Wywoływanie synchronizacji...');
          this._synchronizationService.synchronize();
        }
      },
      error: (err) => {
        this.isLoadingKsefXml = false;
        console.error('Błąd pobierania XML z KSeF:', err);
        this._snackbarService.showSnackBar(
          err?.error?.message || 'Nie udało się pobrać XML z KSeF',
          'error',
          5000
        );
      }
    });
  }
  
  /**
   * Parsuje XML faktury KSeF i wyciąga pozycje oraz dane płatności
   */
  private parseKsefXmlItems(xml: string): void {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      
      // Sprawdź błędy parsowania
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        console.error('Błąd parsowania XML:', parseError.textContent);
        return;
      }
      
      // === PARSOWANIE SYSTEMINFO Z NAGŁÓWKA ===
      this.ksefSystemInfo = this.getXmlValue(xmlDoc.documentElement, 'SystemInfo') || null;

      // === PARSOWANIE POZYCJI FAKTURY ===
      // Pobierz wszystkie elementy FaWiersz (pozycje faktury)
      let items = xmlDoc.getElementsByTagName('FaWiersz');
      if (items.length === 0) {
        items = xmlDoc.getElementsByTagNameNS('*', 'FaWiersz');
      }
      
      this.ksefInvoiceItems = [];
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Pobierz wartości z XML - mogą być netto (P_9A, P_11) lub brutto (P_9B, P_11A)
        const unitNetPriceRaw = parseFloat(this.getXmlValue(item, 'P_9A') || '0');
        const unitGrossPriceRaw = parseFloat(this.getXmlValue(item, 'P_9B') || '0');
        const netValueRaw = parseFloat(this.getXmlValue(item, 'P_11') || '0');
        const grossValueRaw = parseFloat(this.getXmlValue(item, 'P_11A') || '0');
        const vatRate = this.getXmlValue(item, 'P_12') || '';
        const quantity = parseFloat(this.getXmlValue(item, 'P_8B') || '0');
        
        // Oblicz stawkę VAT jako mnożnik (np. 23 -> 0.23)
        // Dla stawek tekstowych (zw, np, oo) lub 0 - vatMultiplier = 0
        let vatMultiplier = 0;
        const vatRateNum = parseFloat(vatRate);
        if (!isNaN(vatRateNum) && vatRateNum > 0) {
          vatMultiplier = vatRateNum / 100;
        }
        
        // Oblicz wartości - priorytet: użyj tego co mamy, dopełnij brakujące
        let unitNetPrice = unitNetPriceRaw;
        let unitGrossPrice = unitGrossPriceRaw;
        let netValue = netValueRaw;
        let grossValue = grossValueRaw;
        
        // PRZYPADEK 1: Mamy tylko wartości brutto (P_9B, P_11A) - oblicz netto
        if (unitNetPrice === 0 && unitGrossPrice > 0) {
          if (vatMultiplier > 0) {
            unitNetPrice = unitGrossPrice / (1 + vatMultiplier);
          } else {
            // Dla stawek zw, np, oo, 0 - netto = brutto
            unitNetPrice = unitGrossPrice;
          }
        }
        if (netValue === 0 && grossValue > 0) {
          if (vatMultiplier > 0) {
            netValue = grossValue / (1 + vatMultiplier);
          } else {
            // Dla stawek zw, np, oo, 0 - netto = brutto
            netValue = grossValue;
          }
        }
        
        // PRZYPADEK 2: Mamy tylko wartości netto (P_9A, P_11) - oblicz brutto
        if (unitGrossPrice === 0 && unitNetPrice > 0) {
          if (vatMultiplier > 0) {
            unitGrossPrice = unitNetPrice * (1 + vatMultiplier);
          } else {
            // Dla stawek zw, np, oo, 0 - brutto = netto
            unitGrossPrice = unitNetPrice;
          }
        }
        if (grossValue === 0 && netValue > 0) {
          if (vatMultiplier > 0) {
            grossValue = netValue * (1 + vatMultiplier);
          } else {
            // Dla stawek zw, np, oo, 0 - brutto = netto
            grossValue = netValue;
          }
        }
        
        // Oblicz VAT jako różnicę
        const vatValue = grossValue - netValue;
        
        // Parsuj kurs waluty (KursWaluty)
        const exchangeRateStr = this.getXmlValue(item, 'KursWaluty');
        const exchangeRate = exchangeRateStr ? parseFloat(exchangeRateStr) : null;

        // Parsuj znacznik StanPrzed (stan przed korektą)
        const stanPrzed = this.getXmlValue(item, 'StanPrzed');
        const isBeforeCorrection = stanPrzed === '1';

        const invoiceItem: KsefInvoiceItem = {
          lp: this.getXmlValue(item, 'NrWierszaFa') || String(i + 1),
          name: this.getXmlValue(item, 'P_7') || '',
          unit: this.getXmlValue(item, 'P_8A') || 'szt.',
          quantity: quantity,
          unitNetPrice: Math.round(unitNetPrice * 100) / 100,
          unitGrossPrice: Math.round(unitGrossPrice * 100) / 100,
          netValue: Math.round(netValue * 100) / 100,
          vatRate: vatRate,
          vatValue: Math.round(vatValue * 100) / 100,
          grossValue: Math.round(grossValue * 100) / 100,
          deliveryDate: this.getXmlValue(item, 'P_6A') || null, // Data dostawy/wykonania usługi
          exchangeRate: exchangeRate, // KursWaluty - kurs waluty
          isBeforeCorrection: isBeforeCorrection, // StanPrzed = 1
        };
        this.ksefInvoiceItems.push(invoiceItem);
      }
      
      // === PARSOWANIE DANYCH PŁATNOŚCI ===
      this.parseKsefPaymentInfo(xmlDoc);
      
      // === PARSOWANIE DANYCH KOREKTY ===
      this.parseKsefCorrectionInfo(xmlDoc);
      
      // === PARSOWANIE DANYCH ZAMÓWIENIA (dla faktur zaliczkowych) ===
      this.parseKsefOrderInfo(xmlDoc);
      
      // === PARSOWANIE RODZAJU FAKTURY ===
      this.parseKsefInvoiceType(xmlDoc);
      
      // === PARSOWANIE OKRESU FAKTURY (OkresFa) ===
      this.parseKsefPeriodInfo(xmlDoc);
      
      // === PARSOWANIE DANYCH ADRESOWYCH I KONTAKTOWYCH PODMIOTÓW ===
      this.parseKsefSubjectsInfo(xmlDoc);
    } catch (error) {
      console.error('Błąd parsowania XML KSeF:', error);
    }
  }
  
  /**
   * Parsuje dane płatności z XML KSeF
   */
  private parseKsefPaymentInfo(xmlDoc: Document): void {
    // Znajdź element Platnosc
    let platnoscElement = xmlDoc.getElementsByTagName('Platnosc')[0];
    if (!platnoscElement) {
      platnoscElement = xmlDoc.getElementsByTagNameNS('*', 'Platnosc')[0];
    }
    
    if (!platnoscElement) {
      this.ksefPaymentInfo = null;
      return;
    }
    
    // Termin płatności (może być w TerminPlatnosci/Termin lub bezpośrednio)
    let paymentDeadline: string | null = null;
    const terminPlatnosciElement = platnoscElement.getElementsByTagName('TerminPlatnosci')[0] 
      || platnoscElement.getElementsByTagNameNS('*', 'TerminPlatnosci')[0];
    if (terminPlatnosciElement) {
      paymentDeadline = this.getXmlValue(terminPlatnosciElement, 'Termin');
    }
    
    // Forma płatności
    const paymentMethodCodeStr = this.getXmlValue(platnoscElement, 'FormaPlatnosci');
    const paymentMethodCode = paymentMethodCodeStr ? parseInt(paymentMethodCodeStr, 10) : null;
    const paymentMethodName = paymentMethodCode !== null 
      ? (KSEF_PAYMENT_METHODS[paymentMethodCode] || `Kod: ${paymentMethodCode}`)
      : 'Nieznana';
    
    // Czy zapłacono (Zaplacono = 1 oznacza zapłacono w całości)
    const zaplaconoStr = this.getXmlValue(platnoscElement, 'Zaplacono');
    const isPaid = zaplaconoStr === '1';
    
    // Data zapłaty
    const paidDate = this.getXmlValue(platnoscElement, 'DataZaplaty');
    
    // Kwota zapłaty (ZnsczenieZaplaty lub ZnaczenieCzesciowej)
    const paidAmountStr = this.getXmlValue(platnoscElement, 'ZnaczenieCzesciowej') 
      || this.getXmlValue(platnoscElement, 'KwotaZaplaty');
    const paidAmount = paidAmountStr ? parseFloat(paidAmountStr) : null;

    // Numer konta bankowego (RachunekBankowy/NrRB)
    let bankAccountNumber: string | null = null;
    const rachunekBankowyElement = platnoscElement.getElementsByTagName('RachunekBankowy')[0]
      || platnoscElement.getElementsByTagNameNS('*', 'RachunekBankowy')[0];
    if (rachunekBankowyElement) {
      bankAccountNumber = this.getXmlValue(rachunekBankowyElement, 'NrRB');
    }
    
    this.ksefPaymentInfo = {
      paymentDeadline,
      paymentMethodCode,
      paymentMethodName,
      isPaid,
      paidDate,
      paidAmount,
      bankAccountNumber
    };
  }
  
  /**
   * Parsuje dane faktury korygującej z XML KSeF
   */
  private parseKsefCorrectionInfo(xmlDoc: Document): void {
    // Znajdź element Fa (główny element faktury)
    let faElement = xmlDoc.getElementsByTagName('Fa')[0];
    if (!faElement) {
      faElement = xmlDoc.getElementsByTagNameNS('*', 'Fa')[0];
    }
    
    if (!faElement) {
      this.ksefCorrectionInfo = null;
      return;
    }
    
    // Pobierz RodzajFaktury
    const invoiceType = this.getXmlValue(faElement, 'RodzajFaktury');
    
    // Jeśli to nie jest korekta, zakończ
    if (!invoiceType || !invoiceType.startsWith('KOR')) {
      this.ksefCorrectionInfo = null;
      return;
    }
    
    // Przyczyna korekty
    const correctionReason = this.getXmlValue(faElement, 'PrzyczynaKorekty');
    
    // Typ korekty
    const correctionTypeStr = this.getXmlValue(faElement, 'TypKorekty');
    const correctionType = correctionTypeStr ? parseInt(correctionTypeStr, 10) : null;
    const correctionTypeName = correctionType !== null 
      ? (KSEF_CORRECTION_TYPES[correctionType] || `Typ: ${correctionType}`)
      : 'Nieznany';
    
    // Dane faktury korygowanej
    let daneFaKorygowanej = faElement.getElementsByTagName('DaneFaKorygowanej')[0];
    if (!daneFaKorygowanej) {
      daneFaKorygowanej = faElement.getElementsByTagNameNS('*', 'DaneFaKorygowanej')[0];
    }
    
    let correctedInvoiceDate: string | null = null;
    let correctedInvoiceNumber: string | null = null;
    let correctedKsefNumber: string | null = null;
    
    if (daneFaKorygowanej) {
      correctedInvoiceDate = this.getXmlValue(daneFaKorygowanej, 'DataWystFaKorygowanej');
      correctedInvoiceNumber = this.getXmlValue(daneFaKorygowanej, 'NrFaKorygowanej');
      correctedKsefNumber = this.getXmlValue(daneFaKorygowanej, 'NrKSeFFaKorygowanej');
    }
    
    this.ksefCorrectionInfo = {
      invoiceType,
      correctionReason,
      correctionType,
      correctionTypeName,
      correctedInvoiceDate,
      correctedInvoiceNumber,
      correctedKsefNumber
    };
  }
  
  /**
   * Parsuje dane zamówienia z XML KSeF (dla faktur zaliczkowych ZAL i KOR_ZAL)
   */
  private parseKsefOrderInfo(xmlDoc: Document): void {
    // Znajdź element Zamowienie w Fa
    let faElement = xmlDoc.getElementsByTagName('Fa')[0];
    if (!faElement) {
      faElement = xmlDoc.getElementsByTagNameNS('*', 'Fa')[0];
    }
    
    if (!faElement) {
      this.ksefOrderInfo = null;
      return;
    }
    
    // Znajdź element Zamowienie
    let zamowienieElement = faElement.getElementsByTagName('Zamowienie')[0];
    if (!zamowienieElement) {
      zamowienieElement = faElement.getElementsByTagNameNS('*', 'Zamowienie')[0];
    }
    
    if (!zamowienieElement) {
      this.ksefOrderInfo = null;
      return;
    }
    
    // Pobierz wartość całego zamówienia
    const orderValueStr = this.getXmlValue(zamowienieElement, 'WartoscZamowienia');
    const orderValue = orderValueStr ? parseFloat(orderValueStr) : 0;
    
    // Pobierz kursy walut z elementu Fa (nie z Zamowienie)
    const kursWalutyZStr = this.getXmlValue(faElement, 'KursWalutyZ');
    const exchangeRate = kursWalutyZStr ? parseFloat(kursWalutyZStr) : null;
    
    const kursWalutyZKStr = this.getXmlValue(faElement, 'KursWalutyZK');
    const exchangeRateBeforeCorrection = kursWalutyZKStr ? parseFloat(kursWalutyZKStr) : null;
    
    // Pobierz kwotę brutto korygowanej faktury w PLN
    const p15ZKStr = this.getXmlValue(faElement, 'P_15ZK');
    const grossAmountBeforeCorrection = p15ZKStr ? parseFloat(p15ZKStr) : null;
    
    // Pobierz pozycje zamówienia (ZamowienieWiersz)
    let orderRows = zamowienieElement.getElementsByTagName('ZamowienieWiersz');
    if (orderRows.length === 0) {
      orderRows = zamowienieElement.getElementsByTagNameNS('*', 'ZamowienieWiersz');
    }
    
    const items: KsefOrderItem[] = [];
    
    for (let i = 0; i < orderRows.length; i++) {
      const row = orderRows[i];
      const netValue = parseFloat(this.getXmlValue(row, 'P_11NettoZ') || '0');
      const vatValue = parseFloat(this.getXmlValue(row, 'P_11VatZ') || '0');
      
      // Parsuj znacznik StanPrzedZ (stan przed korektą)
      const stanPrzedZ = this.getXmlValue(row, 'StanPrzedZ');
      const isBeforeCorrection = stanPrzedZ === '1';
      
      const orderItem: KsefOrderItem = {
        lp: this.getXmlValue(row, 'NrWierszaZam') || String(i + 1),
        name: this.getXmlValue(row, 'P_7Z') || '',
        unit: this.getXmlValue(row, 'P_8AZ') || 'szt.',
        quantity: parseFloat(this.getXmlValue(row, 'P_8BZ') || '0'),
        unitNetPrice: parseFloat(this.getXmlValue(row, 'P_9AZ') || '0'),
        netValue: netValue,
        vatValue: vatValue,
        vatRate: this.getXmlValue(row, 'P_12Z') || '',
        grossValue: netValue + vatValue,
        isBeforeCorrection: isBeforeCorrection
      };
      items.push(orderItem);
    }
    
    this.ksefOrderInfo = {
      orderValue,
      items,
      exchangeRate,
      exchangeRateBeforeCorrection,
      grossAmountBeforeCorrection
    };
  }
  
  /**
   * Parsuje rodzaj faktury z XML KSeF (VAT, ZAL, KOR, KOR_ZAL, etc.)
   */
  private parseKsefInvoiceType(xmlDoc: Document): void {
    let faElement = xmlDoc.getElementsByTagName('Fa')[0];
    if (!faElement) {
      faElement = xmlDoc.getElementsByTagNameNS('*', 'Fa')[0];
    }
    
    if (!faElement) {
      this.ksefInvoiceType = null;
      return;
    }
    
    this.ksefInvoiceType = this.getXmlValue(faElement, 'RodzajFaktury') || 'VAT';
  }
  
  /**
   * Parsuje okres faktury z XML KSeF (OkresFa - P_6_Od i P_6_Do)
   */
  private parseKsefPeriodInfo(xmlDoc: Document): void {
    // Znajdź element OkresFa (może być w Fa)
    let okresFaElement = xmlDoc.getElementsByTagName('OkresFa')[0];
    if (!okresFaElement) {
      okresFaElement = xmlDoc.getElementsByTagNameNS('*', 'OkresFa')[0];
    }
    
    if (!okresFaElement) {
      this.ksefPeriodInfo = null;
      return;
    }
    
    const dateFrom = this.getXmlValue(okresFaElement, 'P_6_Od');
    const dateTo = this.getXmlValue(okresFaElement, 'P_6_Do');
    
    // Tylko jeśli jest przynajmniej jedna data
    if (dateFrom || dateTo) {
      this.ksefPeriodInfo = {
        dateFrom: dateFrom,
        dateTo: dateTo
      };
    } else {
      this.ksefPeriodInfo = null;
    }
  }
  
  /**
   * Parsuje dane adresowe i kontaktowe podmiotów (Podmiot1 = sprzedawca, Podmiot2 = nabywca) z XML KSeF
   */
  private parseKsefSubjectsInfo(xmlDoc: Document): void {
    // Podmiot1 = Sprzedawca
    this.ksefSellerInfo = this._parseSubjectElement(xmlDoc, 'Podmiot1');
    // Podmiot2 = Nabywca
    this.ksefBuyerInfo = this._parseSubjectElement(xmlDoc, 'Podmiot2');
  }

  /**
   * Parsuje dane adresowe i kontaktowe z elementu podmiotu XML
   */
  private _parseSubjectElement(xmlDoc: Document, tagName: string): KsefSubjectInfo | null {
    let subjectEl = xmlDoc.getElementsByTagName(tagName)[0];
    if (!subjectEl) {
      subjectEl = xmlDoc.getElementsByTagNameNS('*', tagName)[0];
    }
    if (!subjectEl) return null;

    // Adres
    let adresEl = subjectEl.getElementsByTagName('Adres')[0];
    if (!adresEl) {
      adresEl = subjectEl.getElementsByTagNameNS('*', 'Adres')[0];
    }

    const countryCode = adresEl ? this.getXmlValue(adresEl, 'KodKraju') : null;
    const addressLine1 = adresEl ? this.getXmlValue(adresEl, 'AdresL1') : null;
    const addressLine2 = adresEl ? this.getXmlValue(adresEl, 'AdresL2') : null;

    // DaneKontaktowe
    let kontaktEl = subjectEl.getElementsByTagName('DaneKontaktowe')[0];
    if (!kontaktEl) {
      kontaktEl = subjectEl.getElementsByTagNameNS('*', 'DaneKontaktowe')[0];
    }

    const email = kontaktEl ? this.getXmlValue(kontaktEl, 'Email') : null;
    const phone = kontaktEl ? this.getXmlValue(kontaktEl, 'Telefon') : null;

    // Zwróć tylko jeśli jest przynajmniej jedna wartość
    if (!countryCode && !addressLine1 && !addressLine2 && !email && !phone) return null;

    return { countryCode, addressLine1, addressLine2, email, phone };
  }

  /**
   * Pobiera wartość z elementu XML
   */
  private getXmlValue(parent: Element, tagName: string): string | null {
    // Próbuj bez namespace
    let element = parent.getElementsByTagName(tagName)[0];
    if (!element) {
      // Próbuj z dowolnym namespace
      element = parent.getElementsByTagNameNS('*', tagName)[0];
    }
    return element?.textContent || null;
  }

  /**
   * Sprawdza czy użytkownik ma dostęp do pełnej historii zmian (PRO/BUSINESS)
   */
  get isFullHistoryAvailable(): boolean {
    if (!this.userSettings?.subscription) return false;
    const subType = this.userSettings.subscription.stage;
    return subType >= 2;
  }

  /**
   * Zwraca stage subskrypcji użytkownika
   */
  get subscriptionType(): number {
    return this.userSettings?.subscription?.stage ?? 0;
  }

  /**
   * Formatuje datę ISO (YYYY-MM-DD) na czytelny format polski (np. "19 styczeń 2026")
   */
  public formatDateHuman(dateStr: string | null): string {
    if (!dateStr) return '';
    
    const dt = DateTime.fromISO(dateStr);
    if (!dt.isValid) return dateStr;
    
    return dt.setLocale('pl').toFormat('d LLLL yyyy');
  }

  /**
   * Formatuje numer konta bankowego (NRB) w czytelny sposób
   * Format: XX XXXX XXXX XXXX XXXX XXXX XXXX (26 cyfr polskiego NRB)
   * Lub dla IBAN: PL XX XXXX XXXX XXXX XXXX XXXX XXXX
   */
  public formatBankAccount(accountNumber: string | null): string {
    if (!accountNumber) return '';
    
    // Usuń wszystkie białe znaki i myślniki
    const clean = accountNumber.replace(/[\s-]/g, '');
    
    // Sprawdź czy zaczyna się od kodu kraju (IBAN)
    const hasCountryCode = /^[A-Z]{2}/.test(clean);
    
    if (hasCountryCode) {
      // IBAN: KK XX XXXX XXXX XXXX XXXX XXXX XXXX
      const countryCode = clean.substring(0, 2);
      const digits = clean.substring(2);
      const formatted = digits.match(/.{1,4}/g)?.join(' ') || digits;
      return `${countryCode} ${formatted}`;
    } else {
      // Polski NRB bez kodu kraju: XX XXXX XXXX XXXX XXXX XXXX XXXX
      const formatted = clean.match(/.{1,4}/g)?.join(' ') || clean;
      // Pierwszy segment to 2 cyfry kontrolne, więc poprawmy format
      if (clean.length === 26) {
        return `${clean.substring(0, 2)} ${clean.substring(2).match(/.{1,4}/g)?.join(' ')}`;
      }
      return formatted;
    }
  }

  /**
   * Zwraca logi widoczne dla użytkownika
   * FREE/START: tylko pierwszy log (CREATE)
   * PRO/BUSINESS: wszystkie logi
   */
  get visibleActivityLogs(): DbActivityLog[] {
    if (this.isFullHistoryAvailable) {
      return this.activityLogs;
    }
    
    // Dla FREE/START - znajdź pierwszy log (CREATE) - to jest ostatni na liście
    // bo logi są posortowane od najnowszego
    const createLog = this.activityLogs.find(log => log.logType === 'CREATE');
    if (createLog) {
      return [createLog];
    }
    
    // Fallback - zwróć ostatni log (najstarszy = utworzenie)
    if (this.activityLogs.length > 0) {
      return [this.activityLogs[this.activityLogs.length - 1]];
    }
    
    return [];
  }

  /**
   * Liczba ukrytych logów (do wyświetlenia w blur overlay)
   */
  get hiddenLogsCount(): number {
    if (this.isFullHistoryAvailable) return 0;
    return Math.max(0, this.activityLogs.length - 1);
  }

  /**
   * Liczba fake logów do pokazania w blur preview (max 3)
   */
  get fakeLogsCount(): number[] {
    const count = Math.min(3, this.hiddenLogsCount);
    return Array.from({ length: count }, (_, i) => i);
  }

  // ========== PŁATNOŚCI ==========
  
  /**
   * Sprawdza czy faktura jest oznaczona jako zapłacona
   */
  get isPaymentReceived(): boolean {
    if (!this.document) return false;
    return DocumentHelper.isPaymentReceived(this.document);
  }
  
  /**
   * Sprawdza czy termin płatności minął
   */
  get isPaymentOverdue(): boolean {
    if (!this.document) return false;
    return DocumentHelper.isDocumentOverdue(this.document);
  }
  
  /**
   * Sprawdza czy można oznaczyć dokument jako opłacony
   * Warunki: status "Do zapłaty" lub source='ksef', NIE jest oznaczony jako RECEIVED
   */
  get canMarkAsPaid(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canMarkAsPaid(this.document);
  }
  
  /**
   * Sprawdza czy można cofnąć oznaczenie płatności
   * Warunki: status "Do zapłaty" lub source='ksef', receivedPayment = RECEIVED
   */
  get canMarkAsUnpaid(): boolean {
    if (!this.document) return false;
    return DocumentHelper.canMarkAsUnpaid(this.document);
  }
  
  /**
   * Oznacza dokument jako opłacony
   */
  markDocumentAsPaid(): void {
    if (!this.document?._id || !this.canMarkAsPaid) return;
    
    this._documentsApiService.updatePaymentStatus(this.document._id, ReceivedPaymentStatus.RECEIVED)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._snackbarService.showSnackBar('Oznaczono jako opłacone', 'success', 3000);
          this._synchronizationService.synchronize('payment-status-update');
        },
        error: (error) => {
          console.error('Błąd aktualizacji statusu płatności:', error);
          this._snackbarService.showSnackBar(
            error?.error?.message || 'Błąd podczas aktualizacji statusu płatności',
            'error',
            5000
          );
        }
      });
  }
  
  /**
   * Cofa oznaczenie płatności
   */
  markDocumentAsUnpaid(): void {
    if (!this.document?._id || !this.canMarkAsUnpaid) return;
    
    this._documentsApiService.updatePaymentStatus(this.document._id, ReceivedPaymentStatus.NOT_RECEIVED)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._snackbarService.showSnackBar('Cofnięto oznaczenie płatności', 'success', 3000);
          this._synchronizationService.synchronize('payment-status-update');
        },
        error: (error) => {
          console.error('Błąd aktualizacji statusu płatności:', error);
          this._snackbarService.showSnackBar(
            error?.error?.message || 'Błąd podczas aktualizacji statusu płatności',
            'error',
            5000
          );
        }
      });
  }

  /**
   * Nawigacja wstecz do listy dokumentów.
   * Jeśli użytkownik przyszedł z listy dokumentów — wróć tam zachowując filtry (query params).
   * W przeciwnym razie — przejdź do czystego /konto/dokumenty/.
   */
  goBack(): void {
    const isDocumentsList = this._previousUrl
      && this._previousUrl.startsWith('/konto/dokumenty')
      && !this._previousUrl.includes('/podglad')
      && !this._previousUrl.includes('/edycja');

    if (isDocumentsList) {
      this._router.navigateByUrl(this._previousUrl!);
    } else {
      this._router.navigate(['/konto/dokumenty']);
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

}

